// =============================================================================
// FILE: packages/renderer/src/components/elite/IdentityTransformation.tsx
// PURPOSE: "Identity Transformation" onboarding. Technician-tier users see an
//          "Unlock Elite Power" panel. Entering a valid RCDD ID triggers a
//          ~2.4s full-screen Warp Drive transition, then permanently re-skins
//          the app to the Gold Enterprise Edition for the 100-day trial and
//          welcomes the operator: "Identity Verified. Welcome home, Commander."
// =============================================================================

import { useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { ShieldCheck, Sparkles, Lock, X, Loader2 } from 'lucide-react';
import { useEliteStore, isValidRcddId } from '../../stores/elite.store.js';
import { playEngineHum, playBeep, haptic } from '../../lib/soundManager.js';

// ── 2.4s warp + reveal overlay ───────────────────────────────────────────────
function TransformationOverlay({ phase, bicsiId }: { phase: 'warp' | 'reveal'; bicsiId: string }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    interface S { nx: number; ny: number; z: number; size: number; hue: number; }
    const mk = (far: boolean): S => { const a = Math.random() * Math.PI * 2; const sp = Math.random() * 0.5 + 0.02; return { nx: Math.cos(a) * sp, ny: Math.sin(a) * sp, z: far ? Math.random() : 0.92 + Math.random() * 0.08, size: Math.random() * 1.8 + 0.6, hue: 42 + Math.random() * 12 }; };
    const stars = Array.from({ length: 260 }, () => mk(true));
    let speed = 0.05, raf = 0, alive = true;
    const draw = (): void => {
      if (!alive) return;
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      if (canvas.width !== w || canvas.height !== h) { canvas.width = Math.max(w, 1); canvas.height = Math.max(h, 1); }
      speed += ((phase === 'warp' ? 0.055 : 0.006) - speed) * 0.05;
      ctx.fillStyle = 'rgba(8,6,2,0.22)'; ctx.fillRect(0, 0, w, h);
      const cx = w * 0.5, cy = h * 0.5, fov = Math.min(w, h) * 0.6;
      for (const s of stars) {
        s.z -= speed;
        if (s.z <= 0.001) { Object.assign(s, mk(false)); continue; }
        const sx = cx + (s.nx / s.z) * fov, sy = cy + (s.ny / s.z) * fov;
        if (sx < -20 || sx > w + 20 || sy < -20 || sy > h + 20) { Object.assign(s, mk(false)); continue; }
        const near = Math.pow(1 - s.z, 1.6); const br = Math.min(1, near * 2.2); const r = Math.max(0.3, s.size * near * 2.2);
        if (speed > 0.004) {
          const pz = Math.min(1, s.z + speed * 5); const px = cx + (s.nx / pz) * fov, py = cy + (s.ny / pz) * fov;
          const g = ctx.createLinearGradient(px, py, sx, sy);
          g.addColorStop(0, `hsla(${s.hue},95%,65%,0)`); g.addColorStop(1, `hsla(${s.hue},95%,72%,${br * 0.7})`);
          ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy); ctx.strokeStyle = g; ctx.lineWidth = r * 0.6; ctx.stroke();
        }
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${s.hue},96%,82%,${br})`; ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [phase]);

  return createPortal(
    <div data-testid="elite-transformation-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10050, background: '#080602', overflow: 'hidden', animation: 'gc-slide-in 0.3s ease' }}>
      <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      {phase === 'reveal' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center', animation: 'gc-slide-in 0.6s ease both' }}>
          <div style={{ width: 74, height: 74, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'radial-gradient(circle at 40% 35%, #4a3a12, #1a1405)', border: '1px solid rgba(252,211,77,0.6)', boxShadow: '0 0 40px rgba(252,211,77,0.4)' }}>
            <ShieldCheck style={{ width: 34, height: 34, color: '#fcd34d' }} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#fde68a', textShadow: '0 0 18px rgba(252,211,77,0.5)' }}>Identity Verified</div>
          <div style={{ fontSize: 15, color: 'rgba(253,230,138,0.85)', letterSpacing: '0.02em' }}>Welcome home, Commander.</div>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(252,211,77,0.6)', marginTop: 4 }}>Enterprise Edition · RCDD #{bicsiId}</div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ── Verify portal ─────────────────────────────────────────────────────────────
function VerifyPortal({ onClose }: { onClose: () => void }): JSX.Element {
  const verify = useEliteStore((s) => s.verify);
  const [id, setId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<null | 'warp' | 'reveal'>(null);

  const submit = () => {
    const clean = id.trim();
    if (!isValidRcddId(clean)) { setErr('Enter a valid RCDD ID (6–8 digit BICSI number).'); playBeep(340); haptic(20); return; }
    setErr(null); setBusy(true);
    playEngineHum(); haptic([12, 30, 80]);
    window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: true } }));
    setPhase('warp');
    window.setTimeout(() => setPhase('reveal'), 1500);
    window.setTimeout(() => {
      verify(clean);   // persist elite state → re-skins app to Gold Enterprise
      window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: false } }));
      onClose();
    }, 2600);
  };

  if (phase) return <TransformationOverlay phase={phase} bicsiId={id.trim()} />;

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10040, background: 'rgba(4,7,13,0.72)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 20 }} onClick={onClose}>
      <div
        data-testid="rcdd-verify-portal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(420px, 100%)', borderRadius: 18, background: 'rgba(10,18,30,0.96)', border: '1px solid rgba(252,211,77,0.35)', boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(252,211,77,0.1)', padding: 26, animation: 'gc-slide-in 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <ShieldCheck style={{ width: 20, height: 20, color: '#fcd34d' }} />
            <span style={{ fontSize: 15, fontWeight: 800, color: '#fde68a' }}>RCDD Verification</span>
          </div>
          <button type="button" data-testid="rcdd-verify-close" data-nosound onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}><X style={{ width: 16, height: 16 }} /></button>
        </div>
        <p style={{ fontSize: 12.5, lineHeight: 1.6, color: 'rgba(200,215,225,0.8)', marginBottom: 18 }}>
          Verified RCDDs receive instant <strong style={{ color: '#fcd34d' }}>Enterprise Edition</strong> access —
          Warp Drive, Autopilot & 1-Click PDF — free for your 100-day trial.
        </p>
        <label style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(148,172,210,0.6)' }}>BICSI RCDD ID</label>
        <input
          data-testid="rcdd-verify-input"
          value={id}
          autoFocus
          onChange={(e) => { setId(e.target.value); setErr(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. 1234567"
          inputMode="numeric"
          style={{ width: '100%', marginTop: 6, marginBottom: err ? 6 : 18, padding: '11px 13px', borderRadius: 11, background: 'rgba(6,14,24,0.9)', border: `1.5px solid ${err ? 'rgba(239,68,68,0.6)' : 'rgba(252,211,77,0.4)'}`, color: '#fde68a', fontSize: 15, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.12em', outline: 'none' }}
        />
        {err && <div data-testid="rcdd-verify-error" style={{ fontSize: 11, color: '#fca5a5', marginBottom: 14 }}>{err}</div>}
        <button
          type="button"
          data-testid="rcdd-verify-submit"
          data-nosound
          onClick={submit}
          disabled={busy}
          style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1px solid rgba(252,211,77,0.6)', background: 'linear-gradient(135deg, rgba(252,211,77,0.22), rgba(180,140,20,0.28))', color: '#fde68a', fontSize: 12.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          {busy ? <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
          {busy ? 'Verifying…' : 'Verify & Unlock Enterprise'}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ── Public: "Unlock Elite Power" panel (shown to Technician-tier users) ────────
export function EliteUnlock(): JSX.Element | null {
  const verified = useEliteStore((s) => s.verified);
  const [open, setOpen] = useState(false);
  if (verified) return null;

  return (
    <>
      <div
        data-testid="elite-unlock-panel"
        className="relative overflow-hidden rounded-2xl p-5 flex items-center gap-4"
        style={{ background: 'linear-gradient(120deg, rgba(30,24,8,0.9), rgba(12,18,28,0.9))', border: '1px solid rgba(252,211,77,0.35)', boxShadow: '0 0 28px rgba(252,211,77,0.1)' }}
      >
        <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(252,211,77,0.12)', border: '1px solid rgba(252,211,77,0.4)' }}>
          <Lock style={{ width: 20, height: 20, color: '#fcd34d' }} strokeWidth={1.9} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold" style={{ color: '#fde68a' }}>Unlock Elite Power</div>
          <div className="text-[12px]" style={{ color: 'rgba(210,220,230,0.75)' }}>Are you RCDD-certified? Verify your credential for instant Enterprise Edition — Warp Drive, Autopilot & 1-Click PDF.</div>
        </div>
        <button
          type="button"
          data-testid="elite-unlock-btn"
          onClick={() => setOpen(true)}
          className="flex-shrink-0 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em]"
          style={{ background: 'linear-gradient(135deg, #fcd34d, #d19a12)', color: '#1a1405', boxShadow: '0 0 18px rgba(252,211,77,0.4)' }}
        >
          <ShieldCheck style={{ width: 15, height: 15 }} /> Verify RCDD
        </button>
      </div>
      {open && <VerifyPortal onClose={() => setOpen(false)} />}
    </>
  );
}
